import { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@vision/ui/hooks';
import { removeRuleCondition, updateCondition, addCondition } from '@vision/ui/store/states/survey-logic-builder-state';
import {
  SurveyLogicCondition,
  SurveyLogicRuleType,
  SurveyLogicControlType,
  SurveyLogicElement,
  SurveyLogicOperator,
} from '@vision/ui/interfaces';
import { buildElementOptions, buildOptionOptions, buildStatementOptions, resolveOptionKey } from '@vision/ui/utils';
import { QuestionStyle } from '@vision/ui/enums';

interface UseConditionBuilderProps {
  ruleId: string;
  groupId: string;
  condition: SurveyLogicCondition;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
}

export function useConditionBuilder({
  ruleId,
  groupId,
  condition,
  elements,
  activeLanguageCode,
}: UseConditionBuilderProps) {
  const { t } = useTranslation('page-survey-logic-builder');
  const { t: tCommon } = useTranslation();
  const dispatch = useAppDispatch();

  const [localAnswerValue, setLocalAnswerValue] = useState(condition.answerValue || '');

  useEffect(() => {
    setLocalAnswerValue(condition.answerValue || '');
  }, [condition.answerValue]);

  const elementOptions = useMemo(() => {
    return buildElementOptions(elements || [], activeLanguageCode);
  }, [elements, activeLanguageCode]);

  const selectedElement = useMemo(() => {
    return (elements || []).find((el) => el.key === condition.elementKey);
  }, [elements, condition.elementKey]);

  const optionOptions = useMemo(() => {
    return buildOptionOptions(selectedElement, activeLanguageCode);
  }, [selectedElement, activeLanguageCode]);

  const resolveOptionKeyForSelected = useCallback(
    (keyOrLabel: string): string | null => {
      return resolveOptionKey(selectedElement, activeLanguageCode, keyOrLabel);
    },
    [selectedElement, activeLanguageCode],
  );

  const statementOptions = useMemo(() => {
    return buildStatementOptions(selectedElement, activeLanguageCode);
  }, [selectedElement, activeLanguageCode]);

  const elementHasOptions = useMemo(() => {
    return (selectedElement?.detail?.options?.length ?? 0) > 0;
  }, [selectedElement]);

  const isMatrixElement = useMemo(() => {
    return selectedElement?.detail?.style === QuestionStyle.MATRIX;
  }, [selectedElement]);

  const isTextElement = useMemo(() => {
    return selectedElement && !elementHasOptions && !isMatrixElement;
  }, [selectedElement, elementHasOptions, isMatrixElement]);

  const textRuleTypeOptions = useMemo(() => {
    return [
      { value: 'equals', label: t('ruleType.equals') },
      { value: 'not_equals', label: t('ruleType.notEquals') },
      { value: 'contains', label: t('ruleType.contains') },
      { value: 'not_contains', label: t('ruleType.notContains') },
      { value: 'regex', label: t('ruleType.regex') },
    ];
  }, [t]);

  const selectedOptionKeys = useMemo(() => {
    const rawKeys = condition.optionKeys?.length
      ? condition.optionKeys
      : condition.optionKey
        ? [condition.optionKey]
        : [];

    return rawKeys
      .map((keyOrLabel) => resolveOptionKeyForSelected(keyOrLabel))
      .filter((key): key is string => key !== null);
  }, [condition.optionKeys, condition.optionKey, resolveOptionKeyForSelected]);

  const selectedStatementKeys = useMemo(() => {
    return condition.statementKeys || (condition.statementKey ? [condition.statementKey] : []);
  }, [condition.statementKeys, condition.statementKey]);

  const getOptionLabel = useCallback(
    (optionKey: string): string => {
      const option = optionOptions.find((opt) => opt.value === optionKey);
      return option?.label || optionKey;
    },
    [optionOptions],
  );

  const optionKeysOperator = condition.optionKeysOperator || 'and';
  const showOptionSelect = !isTextElement;

  const handleUpdate = useCallback(
    (updates: Partial<SurveyLogicCondition>) => {
      dispatch(
        updateCondition({
          ruleId,
          groupId,
          conditionId: condition.id,
          updates,
        }),
      );
    },
    [dispatch, ruleId, groupId, condition.id],
  );

  const handleElementChange = useCallback(
    (value: string | null) => {
      const selectedEl = (elements || []).find((el) => el.key === value);
      const hasOptions = (selectedEl?.detail?.options?.length ?? 0) > 0;
      const isMatrix = selectedEl?.detail?.style === QuestionStyle.MATRIX;
      const controlType: SurveyLogicControlType = hasOptions || isMatrix ? 'option' : 'value';

      handleUpdate({
        elementKey: value || '',
        controlType,
        optionKey: '',
        optionKeys: [],
        statementKey: '',
        statementKeys: [],
        answerValue: '',
      });
    },
    [handleUpdate, elements],
  );

  const handleStatementKeysChange = useCallback(
    (values: string[]) => {
      handleUpdate({
        statementKeys: values,
        statementKey: values[0] || '',
      });
    },
    [handleUpdate],
  );

  const handleRuleTypeChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      handleUpdate({ ruleType: value as SurveyLogicRuleType });
    },
    [handleUpdate],
  );

  const handleOptionKeysChange = useCallback(
    (values: string[]) => {
      handleUpdate({
        optionKeys: values,
        optionKey: values[0] || '',
      });
    },
    [handleUpdate],
  );

  const handleRemoveOption = useCallback(
    (optionKeyToRemove: string) => {
      const newValues = selectedOptionKeys.filter((key) => key !== optionKeyToRemove);
      handleUpdate({
        optionKeys: newValues,
        optionKey: newValues[0] || '',
      });
    },
    [selectedOptionKeys, handleUpdate],
  );

  const handleOptionKeysOperatorToggle = useCallback(() => {
    const currentOperator = condition.optionKeysOperator || 'and';
    const newOperator: SurveyLogicOperator = currentOperator === 'and' ? 'or' : 'and';
    handleUpdate({ optionKeysOperator: newOperator });
  }, [condition.optionKeysOperator, handleUpdate]);

  const handleAnswerValueBlur = useCallback(() => {
    if (localAnswerValue !== condition.answerValue) {
      handleUpdate({ answerValue: localAnswerValue });
    }
  }, [localAnswerValue, condition.answerValue, handleUpdate]);

  const handleDelete = useCallback(() => {
    dispatch(removeRuleCondition({ ruleId, groupId, conditionId: condition.id }));
  }, [dispatch, ruleId, groupId, condition.id]);

  const handleAddCondition = useCallback(() => {
    dispatch(addCondition({ ruleId, groupId }));
  }, [dispatch, ruleId, groupId]);

  return {
    t,
    tCommon,
    elementOptions,
    optionOptions,
    statementOptions,
    textRuleTypeOptions,
    selectedElement,
    selectedOptionKeys,
    selectedStatementKeys,
    isMatrixElement,
    isTextElement,
    showOptionSelect,
    optionKeysOperator,
    localAnswerValue,
    setLocalAnswerValue,
    getOptionLabel,
    handleElementChange,
    handleStatementKeysChange,
    handleRuleTypeChange,
    handleOptionKeysChange,
    handleRemoveOption,
    handleOptionKeysOperatorToggle,
    handleAnswerValueBlur,
    handleDelete,
    handleAddCondition,
  };
}
